/**
 * Cliente HTTP centralizado para el backend FastAPI.
 *
 * Convenciones:
 *  - Base URL "/api" → reescrito a backend real por el proxy Vite (dev)
 *    o servido por nginx/traefik en mismo origen (prod).
 *  - `withCredentials: true` → el browser envía la cookie `sar_session`
 *    en cada request. Sin esto, login no funciona.
 *  - Interceptor de respuesta: si 401, dispara `onUnauthorized` global
 *    (configurado por AuthContext en mount). Evita acoplar axios al
 *    React tree.
 */

import axios, { type AxiosInstance } from "axios";

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

export const api: AxiosInstance = axios.create({
  baseURL: "/api",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Normaliza `detail` a STRING, siempre.
 *
 * FastAPI devuelve dos formas distintas bajo el mismo nombre: un string en las
 * `HTTPException` y un **array de objetos** en los errores de validación (422).
 * El frontend asume string en ~84 sitios y varios lo pintan directo, así que un
 * 422 acababa en `setError([{...}])` y React reventaba con "Objects are not valid
 * as a React child": **pantalla en blanco**, sin pista de la causa (Carlos
 * 2026-08-09, pantalla de Diagnóstico).
 *
 * Se normaliza aquí —un único punto— y no en cada `catch`: el problema es la
 * forma del dato al entrar, no lo que hace cada pantalla con él.
 */
function _detalleLegible(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Errores de validación: "campo: mensaje", que es lo que orienta a quien lee.
    return detail
      .map((e) => {
        const it = e as { loc?: unknown[]; msg?: string };
        const campo = Array.isArray(it?.loc)
          ? it.loc.filter((x) => x !== "body").join(".")
          : "";
        return campo ? `${campo}: ${it?.msg ?? "inválido"}` : (it?.msg ?? JSON.stringify(e));
      })
      .join(" · ");
  }
  return JSON.stringify(detail);
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && onUnauthorized) {
      // Dispara redirect a /login sin recargar la página.
      onUnauthorized();
    }
    const data = error.response?.data;
    if (data && typeof data === "object" && "detail" in data
        && typeof data.detail !== "string") {
      data.detail = _detalleLegible(data.detail);
    }
    return Promise.reject(error);
  },
);


// Errores con causa REAL: prioriza el `detail` del backend sobre el genérico de axios
// (requisito Carlos 2026-07-10: "Request failed with status code 500" no orienta a nadie).
import type { AxiosError } from "axios";

export function apiErrorMessage(err: unknown): string {
  const ax = err as AxiosError<{ detail?: unknown }>;
  const detail = ax?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail) return JSON.stringify(detail);
  if (ax?.response?.status) return `Error ${ax.response.status}: ${ax.message}`;
  return ax?.message ?? String(err);
}
